import React, { useState } from "react";
import { 
  AlertTriangle, Trash2, Info, X, Lock, Shield, 
  CheckCircle2, AlertCircle, Eye, EyeOff 
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/card";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";

const DeleteAccount = () => {
  // Form state
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  
  // List of reasons for deletion
  const reasons = [
    t("deleteAccount.noLongerNeed"),
    t("deleteAccount.foundBetterAlternative"),
    t("deleteAccount.privacyConcerns"),
    t("deleteAccount.tooExpensive"),
    t("deleteAccount.technicalIssues"),
    t("deleteAccount.otherSpecify")
  ];

  // Items that will be deleted
  const deletionItems = [
    t("deleteAccount.profileAndPersonalInfo"),
    t("deleteAccount.projectsAndWorkHistory"),
    t("deleteAccount.messagesAndCommunication"),
    t("deleteAccount.paymentInformation"),
    t("deleteAccount.reviewsAndRatings"),
    t("deleteAccount.portfolioAndFiles")
  ];

  // Handle reason selection
  const handleReasonChange = (selectedReason) => {
    setReason(selectedReason);
    if (selectedReason !== t("deleteAccount.otherSpecify")) {
      setCustomReason("");
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    setError("");
    setIsSubmitting(true);

    try {
      // Validate confirmation text
      if (confirmation.toLowerCase() !== t("deleteAccount.deleteMyAccount")) {
        throw new Error(t("deleteAccount.confirmationError"));
      }

      // Validate password
      if (!password) {
        throw new Error(t("deleteAccount.passwordRequired"));
      }

      // Validate reason
      if (!reason) {
        throw new Error(t("deleteAccount.reasonRequired"));
      }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Handle successful deletion
      // In a real application, you would handle the logout and redirect here
      window.location.href = "/"; // Redirect to homepage after deletion

    } catch (err) {
      setError(err.message || "Failed to delete account. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50"  style={{ textAlign: "left" }}>
      <SharedHeader4 />
      
      <main className="max-w-2xl mx-auto px-4 py-8">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6 text-red-600">
            <AlertTriangle className="h-6 w-6" />
            <h1 className="text-2xl font-bold">{t("deleteAccount.title")}</h1>
          </div>

          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-900">{t("deleteAccount.warningTitle")}</h3>
                <p className="mt-1 text-sm text-red-700">
                  {t("deleteAccount.warningMessage")}
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Step 1: Items to be Deleted */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">{t("deleteAccount.itemsToBeDeletedTitle")}</h2>
                <div className="space-y-3">
                  {deletionItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-700">
                      <X className="h-4 w-4 text-red-500" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={() => setStep(2)}
                variant="outline"
                className="w-full"
              >
                {t("deleteAccount.understandContinue")}
              </Button>
            </div>
          )}

          {/* Step 2: Reason for Deletion */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">{t("deleteAccount.whyDeletingAccount")}</h2>
                <div className="space-y-3">
                  {reasons.map((r) => (
                    <label
                      key={r}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                        reason === r ? "border-blue-500 bg-blue-50" : "border-gray-200"
                      }`}
                    >
                      <input
                        type="radio"
                        name="reason"
                        value={r}
                        checked={reason === r}
                        onChange={() => handleReasonChange(r)}
                        className="sr-only"
                      />
                      <span>{r}</span>
                    </label>
                  ))}
                </div>

                {reason === t("deleteAccount.otherSpecify") && (
                  <textarea
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder={t("deleteAccount.pleaseTellUsMore")}
                    className="mt-4 w-full p-3 border rounded-lg"
                    rows={3}
                  />
                )}
              </div>

              <div className="flex justify-between">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                >
                  {t("deleteAccount.back")}
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!reason || (reason === t("deleteAccount.otherSpecify") && !customReason)}
                >
                  {t("deleteAccount.continue")}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Final Confirmation */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Password Verification */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("deleteAccount.verifyYourPassword")}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border rounded-lg"
                    placeholder={t("deleteAccount.enterYourPassword")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirmation Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("deleteAccount.typeDeleteMyAccount")}
                </label>
                <input
                  type="text"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder={t("deleteAccount.deleteMyAccount")}
                />
              </div>

              <div className="flex justify-between">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                >
                  {t("deleteAccount.back")}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting || 
                    !password || 
                    confirmation.toLowerCase() !== t("deleteAccount.deleteMyAccount")
                  }
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {isSubmitting ? t("deleteAccount.deletingAccount") : t("deleteAccount.deleteAccountButton")}
                </Button>
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="mt-8 pt-6 border-t">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">{t("deleteAccount.needHelp")}</h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t("deleteAccount.needHelpMessage")}
                </p>
                <Button
                  variant="link"
                  className="mt-2 text-blue-600 hover:text-blue-700 p-0"
                  onClick={() => window.location.href = "/support"}
                >
                  {t("deleteAccount.contactSupport")}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </main>
      <br></br>
      <br></br>
      <br></br>
      <br></br>
      <br></br>
      <br></br>
      <br></br>
      
      <div className="mt-auto">
        <SharedFooter3 />
      </div>
    </div>
  );
};

export default DeleteAccount;